// ============================================================================
// mapConfig.ts — ЕДИНАЯ точка конфигурации карт (SSoT/SRP, клиент).
//
// Источник тайлов резолвится ДЕТЕРМИНИРОВАННО (без тихих фоллбэков критериев):
//   1) стиль-файл  (`geo.tiles.style_url`)    — готовый MapLibre-стиль (URL);
//   2) PMTiles     (`geo.tiles.pmtiles_url`)  — один файл (офлайн/контур),
//      допускается `opfs:<имя>` — архив в OPFS (скачан заранее);
//   3) растр XYZ   (`geo.tiles.raster_url`)   — обычные тайлы (по умолчанию OSM).
//
// Настройки читаются через существующий механизм `user_settings`
// (settingsStore) — новых серверных полей НЕ вводится.
// ============================================================================

import { settingsStore } from '@/lib/settingsStore';

/** Настройки (ключи user_settings) — единственная точка правки. */
export const GEO_TILE_SETTINGS = {
  /** URL PMTiles-архива (или `opfs:<имя>` для архива в OPFS). */
  pmtilesUrl: 'geo.tiles.pmtiles_url',
  /** URL растровых XYZ-тайлов (шаблон `{z}/{x}/{y}`). */
  rasterUrl: 'geo.tiles.raster_url',
  /** URL готового MapLibre-стиля (JSON). */
  styleUrl: 'geo.tiles.style_url',
  /** Атрибуция карты (обязательна для OSM — ODbL). */
  attribution: 'geo.tiles.attribution',
} as const;

/** Центр по умолчанию [долгота, широта] — Москва (порядок MapLibre). */
export const GEO_DEFAULT_CENTER: [number, number] = [37.6173, 55.7558];
export const GEO_DEFAULT_ZOOM = 4;

/** Растровые тайлы по умолчанию (OSM; в контуре — свой tile-server URL). */
export const GEO_DEFAULT_RASTER_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

/** Атрибуция по умолчанию (OSM ODbL — обязательна). */
export const GEO_DEFAULT_ATTRIBUTION = '© OpenStreetMap';

export type GeoTileKind = 'style' | 'pmtiles' | 'raster';

export interface GeoTileSource {
  kind: GeoTileKind;
  /** URL стиля / PMTiles-архива (`opfs:<имя>`) / шаблона XYZ-тайлов. */
  url: string;
  /** Векторный источник (стиль/PMTiles) → строим слои; растр → raster-слой. */
  vector: boolean;
  attribution: string;
}

function readSetting(key: string): string {
  try {
    return (settingsStore.get(key) || '').trim();
  } catch {
    return '';
  }
}

/**
 * Резолв источника тайлов из настроек (единая точка).
 * Приоритет: стиль → PMTiles → растр (OSM по умолчанию).
 */
export function resolveGeoTileSource(): GeoTileSource {
  const styleUrl = readSetting(GEO_TILE_SETTINGS.styleUrl);
  const pmtilesUrl = readSetting(GEO_TILE_SETTINGS.pmtilesUrl);
  const rasterUrl = readSetting(GEO_TILE_SETTINGS.rasterUrl);
  const attribution = readSetting(GEO_TILE_SETTINGS.attribution) || GEO_DEFAULT_ATTRIBUTION;

  if (styleUrl) return { kind: 'style', url: styleUrl, vector: true, attribution };
  if (pmtilesUrl) return { kind: 'pmtiles', url: pmtilesUrl, vector: true, attribution };
  return {
    kind: 'raster',
    url: rasterUrl || GEO_DEFAULT_RASTER_URL,
    vector: false,
    attribution,
  };
}

/** Офлайн-архив OPFS: `opfs:<имя>` (см. lib/geo/pmtilesProtocol.ts). */
export function isOpfsTileUrl(url: string): boolean {
  return url.startsWith('opfs:') && url.length > 'opfs:'.length;
}

export function opfsTileName(url: string): string {
  return url.slice('opfs:'.length);
}
